define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/query",
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/api",
	"csi/action/Copy",
	"csi/action/Delete",
	"csi/action/Popup",
	"csi/def",
	"csi/def/Actions",
	"csi/def/Request",
	"csi/def/Rule",
	"csi/def/ViewRef",
	"csi/heading/String",
	"csi/html",
	"csi/locale",
	"csi/store/_base",
	"csi/store/Names",
	"csi/store/Nodes",
	"csi/table/_GroupByRefMixin",
	"csi/table/Copy",
	"csi/table/Delete",
	"csi/widget/MessageDialog",
	"csi/xml",
	"SiteAdmin/SubtypedTable",
	"./util"
], function(declare, lang, query, csi, _, crews, api, Copy, Delete, Popup, def, Actions, Request, Rule, ViewRef, StringRen, html, locale, csiStore, Names, Nodes, _GroupByRefMixin, CopyController, DeleteController, MessageDialog, xml, SubtypedTable, util) {

	var ACTION_COPY = "SpecificationSectionCopy";
	var ACTION_DELETE = "SpecificationSectionDelete";
	var ACTION_EDIT = "SpecificationSectionEdit";
	var ACTION_RELINK = "SpecificationSectionRelink";
	var ACTION_REMOVE = "SpecificationSectionRemove";
	var ACTION_UNLINK = "SpecificationSectionUnlink";
	var ACTION_SHARE = "SpecificationSectionShare";
	var ACTION_PUSH = "SpecificationSectionPush";
	var ACTION_UNLINK_AND_SAVE = "SpecificationSectionUnlinkAndSave";
	var ACTION_REVERT_TO_ORIGINAL = "SpecificationSectionRevertToOriginal";
	var ACTION_APPLY_CHANGES = "SpecificationSectionApplyChanges";

	var ROLE_LIBRARY_ADMIN = "Product Specification Library Administrator";
	var ROLE_LIBRARY_VIEWER = "Product Specification Library Viewer";

	var _isLibraryAdmin;

	const editRule = Object.assign(new Rule(), {
		m_class: "csi-bg-yellow",
		passes: _hasBeenEdited,
	});

	var _GroupRenderer = declare(StringRen, {
		bAllowReporting: true,

		/**
		 * @override
		 * Return DOMNode containing icon
		 */
		getTooltipParams: function(ctx, h, refNode) {
			const params = this.inherited(arguments);
			params.connectNode = query("." + util.CSS_SECTION_ICON, refNode)[0];
			return params;
		},

		/**
		 * Add (un)shared icon to group
		 */
		toHtmlStatic: function(ctx, h, refNode, actionData) {
			const {cache, queryResult} = ctx;
			const section = queryResult && !queryResult.bNewlyCreated && cache.getNode(queryResult.groupValue);
			if (section && (section.$Type == "SpecificationSection")) {
				var bAdHoc = util.isSectionAdHoc(ctx, section);
				if (!bAdHoc) {
					// render definition name in bold
					util.renderSectionDefinition(ctx, section, refNode);

					// render section name and icon unless a non-shared section
					if (util.isSectionShared(ctx, section) || util.isSectionUnlinked(ctx, section) || util.isSectionEdited(ctx, section)) {
						// render section name with count
						this.inherited(arguments);

						// render section icon and apply context to it so tooltip will work
						const iconNode = util.renderSectionIcon(ctx, section, refNode);
						ctx.applyActionContext(iconNode, h.getKeyDP());
					} else {
						// render group count
						html.create("span", {
							innerText: " (" + queryResult.getGroupCount() + ")"
						}, refNode);
					}

					return true;
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * Show tooltip when hovering over (un)shared icon
		 */
		toTooltip: function(ctx, h, tooltip) {
			var refNode = tooltip.getRefNodeForAction();
			var qr = ctx.view.getQueryResultByDomNode(refNode);
			var section = qr && ctx.cache.getNode(qr.groupValue);
			if (section && (section.$Type == "SpecificationSection")) {
				var bShared = util.isSectionShared(ctx, section);
				var bUnlinked = util.isSectionUnlinked(ctx, section);
				var bEdited = util.isSectionEdited(ctx, section);

				if (bShared || bUnlinked || bEdited) {
					tooltip.addContent(html.create("div", {
						innerText: locale.resource(bShared ? "SpecificationSectionTooltipShared" : (bEdited ? "SpecificationSectionTooltipEditMode" : "SpecificationSectionTooltipNotShared"))
					}));

					if (bShared) {
						// add action for Where Used
						var a = lang.mixin(new Popup(), {
							classControllerParams: {
								popupForceBrowse: true,
								popupLinkTarget: "_blank"
							},
							m_id: "SpecificationSectionSharedWhereUsed",
							m_viewRef: lang.mixin(new ViewRef(), {
								m_id: "WhereUsed",
								m_url: section.$URL
							})
						});
						a.resolve();

						tooltip.addAction(a);
					}
				}
			}
		},

		/**
		 * Do not allow editing of group name
		 */
		_getAction: function() {
			return null;
		}
	});

	var _CopySectionController = declare(CopyController, {
		/**
		 * @override
		 * Refresh the Widget
		 */
		postProcessSuccess: function() {
			this.widget.refreshWidget();
		}
	});

	var _DeleteSectionController = declare(DeleteController, {
		/**
		 * @override
		 * Add confirmation message
		 */
		confirmAction: function(params) {
			params.message = locale.resource("SpecificationSectionDelete_Confirm");
			this.inherited(arguments);
		}
	});

	/**
	 * Return section for given group action context
	 * @param {Context} ctx
	 * @return {Node}
	 */
	function _getSectionFromActionContext(ctx) {
		const {cache, queryResult} = ctx;
		return cache.get(queryResult.groupValue);
	}

	/**
	 * Return true for SID and SSI attributes modified by client
	 * @param {Node} node
	 * @param {Cache} cache
	 * @param {Context} ctx
	 * @param {Heading} h
	 * @return {boolean}
	 */
	function _hasBeenEdited(node, cache, ctx, h) {
		var sdi = ctx.getNodeContext();
		if (!sdi) {
			// this case is for not having sdi context
			return false;
		}
		var key = h.m_id;
		var original = cache.getNode(node.Original);
		if (util.isSectionEdited(ctx, cache.getNode(sdi.Section))) {
			if (util.isDataSheetItemNewInEditedSection(ctx, sdi)) {
				// this case is solved by Rule on Relationship
				return false;
			} else if (original && (node.$Type === util.TYPE_SSI)) {
				// only highlight if section item or ad-hoc definition changed; do not highlight changes to definitions from library
				return !xml.isSameValue(original[key], node[key]);
			} else if (original && (node.$Type === util.TYPE_SID) && util.isDefinitionAdHoc(ctx, node)) {
				// this case is to handle ad-hoc sid definition changes
				return util.isDifferentFromBaselineDefinition(ctx, sdi, key);
			}
		}
		return false;
	}

	/**
	 * Return true if not in edited section, or if in edited section and user has library access
	 * @param {Node} node
	 * @param {Cache} cache,
	 * @param {Context} ctx
	 * @return {boolean}
	 */
	function _isEditableItem(node, cache, ctx) {
		var section = ctx.cache.getNode(node.Section);
		return !util.isSectionEdited(ctx, section) || _isLibraryAdmin;
	}

	/**
	 * Return true if node is a new DSI in an edited section
	 */
	function _isNewItemInEditedSection(node, cache, ctx) {
		// protect against action rows where node is null
		return node && util.isDataSheetItemNewInEditedSection(ctx, node);
	}

	return declare([SubtypedTable, _GroupByRefMixin], {
		groupByRefAttr: "Section",
		groupRendererClass: _GroupRenderer,
		newActionsButtonResource: "Create",
		placeholderActions: ["AggregateSpecificationSectionDefinition", "AddSharedSpecificationSection", "NewSpecificationSection"],

		/**
		 * @override - For this class enable new actions if table is grouped and there are no results
		 */
		bDisableNewActionIfNoGroups: false,

		/**
		 * @property {boolean} bPrintResetCache - set to true since the various unlink/relink/edit section actions cause problems
		 * with dynamically updating group values stores (does not support replacing a value)
		 */
		bPrintResetCache: true,

		/** @property {Actions} _specificationSectionActions - actions for section group */
		_specificationSectionActions: null,

		/**
		 * Initialize newActionsButtonGroups
		 */
		postCreate: function() {
			this.newActionsButtonGroups = [locale.typePlural("SpecificationItemDefinition"), locale.resource("Sections")];

			this.inherited(arguments);
		},

		/**
		 * Return true if edited attribute is in mapping for subtype, as the ResetMappedAttributes action may need to be redrawn
		 */
		hasRefNodeDependency: function(node, heading, typeAttr) {
			return util.isAttributeInSectionItemAttributeMap(heading.m_id, node, this.cacheView) || this.inherited(arguments);
		},

		/**
		 * Return true is given action should be disabled
		 * @param {Action} a
		 */
		disableNewAction: function(a) {
			return !this.haveEditableSection();
		},

		/**
		 * Return true if view contains editable sections
		 * @return {boolean}
		 */
		haveEditableSection: function() {
			var qrRoot = this.getQueryResult();
			var cache = this.getCache();
			var qrChilds = qrRoot.childResults;
			var ctx = qrRoot.getChildContext();
			return _.some(qrChilds, function(qr) {
				var group = qr.groupValue;
				var section = group && cache.getNode(group);
				return section && this.isEditableSection(ctx, null, section);
			}, this);
		},

		/**
		 * @override
		 * This view is always grouped by Section, so find Existing group row to add new row to it
		 * otherwise create "Newly Created" group row.
		 */
		generateNewlyCreatedRow: function(newNodes, bBlanks) {
			var tr = this.newlyCreatedNode;
			if (tr) {
				return tr;
			}

			// Find group for section referenced by new nodes
			var qrRoot = this.getQueryResult();
			var node = newNodes && newNodes.length && newNodes[0];
			var qrGroup = _.findWhere(qrRoot.childResults, {groupValue: node.Section});
			var groupRow = qrGroup && this.getRefNodeById(qrGroup.id);
			if (groupRow) {
				return groupRow;
			} else {
				// Create new Newly Created
				return this.inherited(arguments);
			}
		},

		/**
		 * Return list of this view' editable sections
		 * @return {Object[]}
		 */
		getEditableSectionsStore: function() {
			var qrRoot = this.getQueryResult();
			var cache = this.getCache();
			var qrChilds = qrRoot.childResults;
			var ctx = qrRoot.getChildContext();
			var sections = [];
			_.each(qrChilds, function(qr) {
				var group = qr.groupValue;
				var section = group && cache.getNode(group);
				if (section && this.isEditableSection(ctx, null, section)) {
					var sectionName = section[xml.ATTR_NAME];
					if (!sectionName) {
						var sectionDefinition = cache.getNode(section.Definition);
						sectionName = sectionDefinition[xml.ATTR_NAME];
					}
					sections.push(csiStore.nameObject(sectionName, section.$URL));
				};
			}, this);
			return new Names({data: sections, bSort: false});
		},

		/**
		 * Return true if shared section is being edited and user has library access; otherwise, return true for sections that are not shared
		 * @param {Context} ctx
		 * @param {Node} [node] - node is passed when checking access to row actions for the node
		 * @param {Node} [parent] - parent is passed when checking access to New actions under the parent row
		 * @return {boolean}
		 */
		isEditableSection: function(ctx, node, parent) {
			// if parent exists, then it will be the section (from group value); otherwise, node with be SDI
			var section = parent || ctx.cache.getNode(node.Section);

			if (util.isSectionEdited(ctx, section)) {
				return _isLibraryAdmin;
			} else if (!util.isSectionShared(ctx, section)) {
				return true;
			}

			return false;
		},

		/**
		 * Always return true as it has no CV configuration on grouping and always grouped by section
		 * @override
		 */
		isGroupBySection: function(allowTwoGroups) {
			return true;
		},

		/**
		 * Return true if item attributes have been changed after mapping from library
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		isItemChangedFromMapping: function(ctx, action) {
			var sdi = ctx.getNode();
			var ssi = ctx.cache.getNode(sdi.Actual);
			return xml.nodeHasAccess(ssi, xml.RIGHT_WRITE) && util.isSectionItemChangedFromMapping(ctx, ssi);
		},

		/**
		 * Return true if section was not originally created from a shared section
		 * @param {Context} ctx
		 * @param {Node} section
		 * @return {boolean}
		 */
		isNonSharedSection: function(ctx, section) {
			return !util.isSectionShared(ctx, section) && !util.isSectionUnlinked(ctx, section);
		},

		/**
		 * Indicate that we want to render disabled row actions, and specify rule used to control access to editing attributes in edited sections
		 */
		onCreateMainQueryLevel: function(ql) {
			var r = new Rule();
			r.passes = _isEditableItem;
			// If in system configuration we set the view to be read only - ql.actions will be null
			// Setup->User Management -> Views -> Class = "Spec Data Sheet Revision" and then uncheck "Editable" for Items. 
			if (ql.actions) {
				ql.actions.m_rules = [r];
			}
			ql.bRenderDisabledActions = true;
		},

		/**
		 * Configure query level for group
		 * @param {Level} ql
		 */
		onGroupByRefQueryLevel: function(ql) {
			const node = this.getViewNode();
			if (node) {
				const data = this.cacheView.getNodes(node.AllSections);
				const store = new Nodes({data});

				ql.actions = this._specificationSectionActions;
				ql.groupDefaultsStore = store;
			}
		},

		/**
		 * Signal view error if does not have access to library
		 */
		onViewStartBuild: function() {
			if (!crews.isInGlobalRoles([ROLE_LIBRARY_ADMIN, ROLE_LIBRARY_VIEWER])) {
				this.set("contentMessage", locale.resource("SpecificationDataSheetPermissionError"));
			}
		},

		/**
		 * Initialize view grouping and group actions
		 */
		_resolveView: function() {
			var defView = this.defView;

			// add dependency path to get all sections to support grouping
			defView.m_depPaths = defView.m_depPaths || [];
			defView.m_depPaths.push("Child:AllSections/Child:Definition");

			// cache access to library
			_isLibraryAdmin = crews.isInGlobalRoles([ROLE_LIBRARY_ADMIN]);

			// prepare actions for Section group
			// NOTE: for all section actions, the context parent is SpecificationDataSheetRevision which is why we use m_rightParent="Write"
			var editSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "EditSpecificationSection"});
			var copySectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "CopySpecificationSection"});
			var relinkSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "RelinkSpecificationSection"});
			var removeSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "RemoveSpecificationSection"});
			var unlinkSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "UnlinkSpecificationSection"});
			var shareSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "ShareSpecificationSection"});
			var pushSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "PushSharedSpecificationSection"});
			var editSaveAsUnlinkedSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "EditSaveAsUnlinkedSpecificationSection"});
			var editRestoreSharedSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "EditRestoreSharedSpecificationSection"});
			var editApplyToSharedSectionRequest = lang.mixin(new Request(), {m_module: "SpecificationDataSheet", m_operation: "EditApplyToSharedSpecificationSection"});

			this._specificationSectionActions = _.extend(new Actions(), {
				m_actions: [
					defView._addViewAction(ACTION_EDIT, {
						classController: "csi/action/GroupController",
						m_accessAction: "_accessActionEditSection",
						m_request: editSectionRequest,
						m_postAction: "_postActionEdit",
						m_rightParent: xml.RIGHT_WRITE
					}),
					defView._addViewAction(ACTION_PUSH, {
						classController: "SpecificationDataSheet.Core.SectionPushController",
						m_accessAction: "_accessActionPushSection",
						m_request: pushSectionRequest,
						m_viewRef: lang.mixin(new ViewRef(), {m_id: "PushSharedSpecificationSectionWizard"})
					}),
					defView._addViewAction(ACTION_SHARE, {
						classController: "csi/action/GroupController",
						m_accessAction: "_accessActionShareSection",
						m_viewRef: lang.mixin(new ViewRef(), {
							m_id: "ShareSpecificationSectionForm"
						}),
						m_onAction: "_onActionSectionShare",
						m_postAction: "postActionRefresh",
						m_request: shareSectionRequest,
						m_rightParent: xml.RIGHT_WRITE
					}),
					defView._addViewAction(ACTION_UNLINK, {
						classController: "csi/action/GroupController",
						m_accessAction: "_accessActionUnlinkSection",
						m_postAction: "_postActionUnlink",
						m_request: unlinkSectionRequest,
						m_rightParent: xml.RIGHT_WRITE
					}),
					defView._addViewAction(ACTION_RELINK, {
						classController: "csi/action/GroupController",
						confirmAction: "SpecificationSectionRelink",
						confirmIcon: MessageDialog.ICON_ERROR,
						m_accessAction: "_accessActionRelinkSection",
						m_preAction: "_preActionRelink",
						m_postAction: "_postActionRelink",
						m_request: relinkSectionRequest,
						m_rightParent: xml.RIGHT_WRITE
					}),
					defView._addViewAction(new Copy(), {
						classController: _CopySectionController,
						bEllipses: true,
						returnParams: {},
						m_id: ACTION_COPY,
						m_accessAction: "_accessActionCopySection",
						m_request: copySectionRequest
					}),
					defView._addViewAction(new Delete(), {
						bEllipses: true,
						confirmAction: MessageDialog.BUTTON_REMOVE,
						m_id: ACTION_REMOVE,
						m_accessAction: "_accessActionRemoveSection",
						m_request: removeSectionRequest
					}),
					defView._addViewAction(new Delete(), {
						classController: _DeleteSectionController,
						bEllipses: true,
						m_id: ACTION_DELETE,
						m_accessAction: "_accessActionDeleteSection",
						m_request: removeSectionRequest
					}),
					this.defView._addViewAction(ACTION_UNLINK_AND_SAVE, {
						classController: "csi/action/GroupController",
						m_typeControl: def.ACTION_ICON_BUTTON,
						iconButtonClass: "csi-table-group-secondary-btn csi-icon-link-off",
						m_class: "csiActionIcon csi-icon-link-off",
						m_accessAction: "_accessActionSectionEditButtons",
						m_postAction: "_postActionUnlinkAndSave",
						m_request: editSaveAsUnlinkedSectionRequest,
						m_rightParent: xml.RIGHT_WRITE
					}),
					this.defView._addViewAction(ACTION_REVERT_TO_ORIGINAL, {
						classController: "csi/action/GroupController",
						m_typeControl: def.ACTION_ICON_BUTTON,
						dependencyPaths: [xml.path("Baseline", "Original")],
						materialIcon: "undo",
						m_accessAction: "_accessActionSectionEditButtons",
						m_onAction: "_onActionOnSectionWithBaseline",
						m_postAction: "_postActionOnSectionWithBaseline",
						m_request: editRestoreSharedSectionRequest,
						m_rightParent: xml.RIGHT_WRITE
					}),
					this.defView._addViewAction(ACTION_APPLY_CHANGES, {
						classController: "csi/action/GroupController",
						m_typeControl: def.ACTION_ICON_BUTTON,
						dependencyPaths: [xml.path("Baseline", "Original")],
						iconButtonClass: "csi-table-group-primary-btn",
						materialIcon: "check",
						m_accessAction: "_accessActionSectionEditButtons",
						m_onAction: "_onActionOnSectionWithBaseline",
						m_postAction: "_postActionOnSectionWithBaseline",
						m_request: editApplyToSharedSectionRequest,
						m_rightParent: xml.RIGHT_WRITE
					})
				]
			});

			api.excludeActionFromEllipses(ACTION_UNLINK_AND_SAVE);
			api.excludeActionFromEllipses(ACTION_REVERT_TO_ORIGINAL);
			api.excludeActionFromEllipses(ACTION_APPLY_CHANGES);

			// add Rule to highlight new rows in edited section
			this.relRoot.m_rules = this.relRoot.m_rules || [];
			this.relRoot.m_rules.push(lang.mixin(new Rule()), {
				m_class: "csi-bg-yellow",
				passes: _isNewItemInEditedSection
			});

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Apply highlight rule to SID, SSI attributes when a section is in edit mode
		 */
		_resolveAttributeSpecs() {
			this.inherited(arguments);

			this.attributeSpecs.traverse(false, ({heading}) => {
				const {ownerType} = heading;

				if (ownerType === util.TYPE_SSI || ownerType === util.TYPE_SID) {
					const rules = heading.m_rules ||= [];
					if (!rules.includes(editRule)) {
						rules.push(editRule);
					}
				}
			});
		},

		/**
		 * Return true if section is shared and not in edit mode
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionEditSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && util.isSectionShared(ctx, section) && !util.isSectionEdited(ctx, section) && xml.nodeHasAccess(section, xml.RIGHT_ADMIN);
		},

		/**
		 * Return true if section is shared and has permission
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionPushSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && section.Active && util.isSectionShared(ctx, section) && !util.isSectionEdited(ctx, section);
		},

		/**
		 * Return true if section is ad-hoc or unlinked
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionShareSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && !util.isSectionShared(ctx, section) && !util.isSectionEdited(ctx, section) && _isLibraryAdmin;
		},

		/**
		 * Return true if section is shared and not in edit mode
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionUnlinkSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && util.isSectionShared(ctx, section) && !util.isSectionEdited(ctx, section);
		},

		/**
		 * Return true if section is unlinked
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionRelinkSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && util.isSectionUnlinked(ctx, section) && !util.isSectionEdited(ctx, section);
		},

		/**
		 * Return true if section is ad-hoc
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionCopySection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && util.isSectionAdHoc(ctx, section) && !util.isSectionEdited(ctx, section);
		},

		/**
		 * Return true if section is shared
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionRemoveSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && util.isSectionShared(ctx, section) && !util.isSectionEdited(ctx, section);
		},

		/**
		 * Return true if section is ad-hoc or unlink
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionDeleteSection: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return section && !util.isSectionShared(ctx, section) && !util.isSectionEdited(ctx, section);
		},

		/**
		 * Return true if changes to edited section can be applied
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionSectionEditApply: function(ctx, action) {
			return _isLibraryAdmin && this._accessActionSectionEditButtons(ctx, action);
		},

		/**
		 * Return true if section is shared and in edit mode
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionSectionEditButtons: function(ctx, action) {
			var section = _getSectionFromActionContext(ctx);
			return util.isSectionEdited(ctx, section);
		},

		/**
		 * Refresh section and item rows when edited
		 * @param {Controller} ac
		 */
		_postActionEdit: function(ac) {
			var sections = ac.m_response.getNodesOfType("SpecificationSection");
			var editSection = _.find(sections, function(section) {
				return util.isSectionEdited(null, section);
			});

			this._refreshSection(ac, editSection);
		},

		/**
		 * Store URL of shared section for use in PostAction
		 * @param {Controller} ac
		 * @return {boolean}
		 */
		_preActionRelink: function(ac) {
			ac.urlSharedSection = ac.getNode().Original;
			return true;
		},

		/**
		 * Refresh section and item rows when relinked
		 * @param {Controller} ac
		 */
		_postActionRelink: function(ac) {
			var sharedSection = ac.m_cache.getNode(ac.urlSharedSection);
			this._refreshSection(ac, sharedSection);
		},

		/**
		 * Refresh section and item rows when unlinked
		 * @param {Controller} ac
		 */
		_postActionUnlink: function(ac) {
			var urlSharedSection = ac.m_urlNode;
			var sections = ac.m_response.getNodesOfType("SpecificationSection");
			var unlinkedSection = _.find(sections, function(section) {
				return section.Original == urlSharedSection;
			});
			this._refreshSection(ac, unlinkedSection);
		},

		/**
		 * Refresh section and item rows when unlinked from edit section
		 * @param {Controller} ac
		 */
		_postActionUnlinkAndSave: function(ac) {
			var unlinkedSection = ac.m_response.getNodeOfType("SpecificationSection");
			this._refreshSection(ac, unlinkedSection);
		},

		/**
		 * Store URL of shared section for use in PostAction
		 * @param {Controller} ac
		 */
		_onActionOnSectionWithBaseline: function(ac) {
			var baselineNode = ac.m_cache.getNode(ac.getNode().Original);
			ac.urlSharedSection = baselineNode.Original;
		},

		/**
		 * Refresh section and item rows when relinked from edit section
		 * @param {Controller} ac
		 */
		_postActionOnSectionWithBaseline: function(ac) {
			var baselineSection = ac.m_cache.getNode(ac.urlSharedSection);
			this._refreshSection(ac, baselineSection);
		},

		/**
		 * Save the specification section name and validate its uniqueness
		 * @param {Controller} ac
		 */
		_onActionSectionShare: function(ac) {
			ac.toWriterFields(ac.m_urlNode);
			ac.m_writer.validateNode(ac.m_urlNode, true);
		},

		/**
		 * Refresh section (group) rows
		 * @param {Controller} ac
		 * @param {Node} section
		 */
		_refreshSection: function(ac, section) {
			var qr = ac.qrGroup;

			// find data sheet items in section or from query result
			var dataSheetItems = ac.m_response.getNodesOfType("SpecificationDataSheetItem") || this.cacheView.getNodes(qr.getChildResultUrls());
			dataSheetItems = _.compact(dataSheetItems);
			var hash = csi.hashArray(dataSheetItems, "Actual", "$URL");
			var dataSheetItemUrls = _.map(section.SectionItems, function(url) {
				return hash[url];
			});

			// update query result with new group value and results
			qr.resetGroupValue(section.$URL, section.$URL, dataSheetItemUrls);

			// refresh rows
			this.postActionRefreshHierarchyRow(ac);
		},

		/**
		 * Reset levels when redrawing with new data so that groups are processed correctly
		 */
		_redrawNewData: function() {
			this._resetLevels();
			this.inherited(arguments);
		},

		/**
		 * Reset levels when refreshing data so that groups are processed correctly
		 */
		_refreshData: function() {
			this._resetLevels();
			this.inherited(arguments);
		},

		/**
		 * Return custom message for no result placeholder in the table
		 * @return {string}
		 */
		_getNoResultDescription: function(hasColumnFilter, hasNewAction) {
			return locale.resource("SpecificationDataSheetRevisionNoResults");
		}
	});
});
